import type { IAttentionStatusApi } from '../api/types'
import type { IRaisedAttentionStatus } from '../model/types'

/**
 * Whether the mask raises the catalog row's bit. Division rather than `&`, because the bitwise
 * operators coerce to int32 and the catalog's bits are free to grow past it.
 */
export const isAttentionStatusBitRaised = (mask: number, bitValue: number): boolean =>
  bitValue > 0 && Math.floor(mask / bitValue) % 2 === 1

interface IDecodeAttentionStatusMaskParams {
  /** One offering's mask of a partner; `0`, `null` or a fractional value raises nothing. */
  mask: number | null | undefined
  catalog: IAttentionStatusApi[]
  /** Offering the titles are stated for; replaces the payload's `Offering X` placeholder. */
  offering?: string
}

/**
 * The catalog rows a partner's attention mask raises, ordered by `SortOrder`.
 * Bits the catalog does not name are ignored, so a newer backend cannot break the widget.
 */
export const decodeAttentionStatusMask = ({
  mask,
  catalog,
  offering,
}: IDecodeAttentionStatusMaskParams): IRaisedAttentionStatus[] => {
  if (!Number.isInteger(mask) || (mask as number) <= 0) {
    return []
  }

  return [...catalog]
    .filter(({ BitValue }) => isAttentionStatusBitRaised(mask as number, BitValue))
    .sort((left, right) => left.SortOrder - right.SortOrder)
    .map(({ Title, BitValue, Severity, SortOrder }) => ({
      bitValue: BitValue,
      title: offering ? Title.replace('Offering X', offering) : Title,
      titleTemplate: Title,
      severity: Severity,
      sortOrder: SortOrder,
      offerings: offering ? [offering] : [],
      condition: '',
    }))
}
